import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import toastCommonProps from "../../../../common/toast";
import { getNickname } from "../../../../features/mobile/questionHealthSlice";
import { saveLoginInfo } from "../../../../features/mobile/commonSlice";
import api from "../../../../common/api";
import { generateRandomNickname } from "../../../../common/NickNameValue";
import HeadCont from "../../components/common/HeadCont";
import Btn from "../../components/common/Btn";
import InputBox from "../../components/common/InputBox";
import TitleBox from "../../components/common/TitleBox";
import PassInput from "../../components/common/PassInput";
import CharacterInput from "../../components/sign/CharacterInput";
import SignUpSurvey from "../../components/survey/SignUpSurvey";

const EmailSignUp = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const dummyNickname = useSelector((state) => state.questionHealth?.dummyNickname) || generateRandomNickname();

  const [signupDatas, setSignupDatas] = useState({
    email: "",
    password: "",
    nickName: "",
    characterPhoto: "",
    firebaseToken: window.sessionStorage.getItem("token") || null,
    userId: null, // Will be set after user creation in step 3
  });

  const [step, setStep] = useState(1); // 1: Email, 2: Email Verification & Password, 3: Terms & Agreements (save to DB), 4: Survey, 5: Nickname, 6: Character
  const [subStep, setSubStep] = useState(1); // For step 2: 1 = verification, 2 = password
  const [active, setActive] = useState("ready");
  const [errorMsg, setErrorMsg] = useState(null);
  const [surveyResponse, setSurveyResponse] = useState(null);
  const [verificationToken, setVerificationToken] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);

  const emailRef = useRef(null);
  const verificationRef = useRef(null);

  const onSurveyInput = (value) => {
    setSurveyResponse({
      selectedNum: value.selectedNum,
      selectedAnswer: value.selectedAnswer,
      etc: value.etc,
    });
  };

  const onChangeInput = (e, field) => {
    if (field === 'verificationCode') {
      // Only allow numbers and limit to 6 digits
      const numericValue = e.target.value.replace(/\D/g, '').slice(0, 6);
      setVerificationCode(numericValue);
      setErrorMsg(null);
      // Enable button when 6 digits are entered
      setActive(numericValue.length === 6 ? "on" : "ready");
    } else {
      setSignupDatas(prev => ({ ...prev, [field]: e.target.value }));
      setErrorMsg(null);

      if (step === 1 && field === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(e.target.value)) {
          setActive("on");
        } else {
          setActive("ready");
        }
      } else if (step === 3) {
        if (signupDatas.password && e.target.value) {
          setActive("on");
        }
      } else if (step === 5 && field === 'nickName') {
        setActive(e.target.value.length > 0 ? "on" : "ready");
      }
    }
  };

  const onSetNextStep = async () => {
    if (step === 1) {
      // STEP 1: Email Input
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(signupDatas.email)) {
        setErrorMsg(t('signup.invalid_email', 'Please enter a valid email address'));
        return;
      }

      setActive("loading");

      // Check if email is already registered and which method was used
      try {
        const checkRes = await api.post('/auth/email/check', { email: signupDatas.email });

        if (checkRes.data.ok && !checkRes.data.available) {
          // Tell user which method (Email or Google) this email is registered with
          const accountType = checkRes.data.accountType;
          const methodName = accountType === 'google' ? 'Google' : 'Email';
          setErrorMsg(`This email is already registered using a ${methodName} account.`);
          setActive("ready");
          return;
        }

        // If email was verified but user didn't complete registration (no Terms agreed),
        // delete the incomplete data
        if (checkRes.data.emailVerified && !checkRes.data.termsAgreed) {
          await api.post('/auth/email/cleanup-incomplete', { email: signupDatas.email });
        }
      } catch (err) {
        console.error('Error checking email:', err);
      }

      // Send verification email
      try {
        const verifyRes = await api.post('/auth/verify-email/send', { email: signupDatas.email });

        if (verifyRes.data.ok) {
          toast(
            <p>{t('signup.verification_sent', 'Verification email sent! Please check your email.')}</p>,
            toastCommonProps("bottom-center", "toast_success", 2000)
          );
          setStep(2);
          setActive("ready");
        } else {
          setErrorMsg(verifyRes.data.error || t('signup.verification_failed', 'Failed to send verification email'));
          setActive("ready");
        }
      } catch (err) {
        console.error('Error sending verification email:', err);
        setErrorMsg(t('signup.verification_failed', 'Failed to send verification email'));
        setActive("ready");
      }
    } else if (step === 2) {
      // STEP 2: Email Verification & Password
      if (subStep === 1) {
        // Sub-step 1: Verify email code
        setActive("loading");

        try {
          const verifyRes = await api.get(`/auth/verify-email?token=${verificationCode}`);

          if (verifyRes.data.ok) {
            setEmailVerified(true);
            toast(
              <p>{t('signup.email_verified', 'Email verified successfully!')}</p>,
              toastCommonProps("bottom-center", "toast_success", 1500)
            );
            setSubStep(2); // Move to password input
            setActive("ready");
          } else {
            setErrorMsg(verifyRes.data.error || t('signup.invalid_verification_code', 'Invalid verification code'));
            setActive("ready");
          }
        } catch (err) {
          console.error('Error verifying email:', err);
          setErrorMsg(t('signup.invalid_verification_code', 'Invalid or expired verification code'));
          setActive("ready");
        }
      } else if (subStep === 2) {
        // Sub-step 2: Password collection - handled by PassInput
        // This will trigger step 3 (Terms & Agreements)
        setStep(3);
        setActive("ready");
      }
    } else if (step === 3) {
      // STEP 3: Terms & Agreements - CREATE USER ACCOUNT HERE
      // This is triggered by PassInput's onSaveAgreement callback
      // The onSaveAgreement function will handle this
      // User account is saved to database after terms are agreed
    } else if (step === 4) {
      // STEP 4: Survey
      setStep(5);
      setActive("ready");
    } else if (step === 5) {
      // STEP 5: Nickname
      const finalNickname = signupDatas.nickName || dummyNickname;
      setSignupDatas(prev => ({ ...prev, nickName: finalNickname }));
      setStep(6);
      setActive("ready");
    } else if (step === 6) {
      // STEP 6: Character - Update existing user with final info
      try {
        const updateParams = {
          userId: signupDatas.userId,
          nickName: signupDatas.nickName || dummyNickname,
          characterPhoto: signupDatas.characterPhoto,
        };

        const res = await api.post('/user/updateProfile', updateParams);

        console.log('Update profile response:', res.data);

        if (res.data.ok) {
          // Update Redux state with the final nickname and character
          dispatch(saveLoginInfo({
            userId: signupDatas.userId,
            nickName: signupDatas.nickName || dummyNickname,
            email: signupDatas.email,
            characterPhoto: signupDatas.characterPhoto,
            existPass: true,
          }));

          // Clean up temporary session storage
          sessionStorage.removeItem('tempSignupEmail');
          sessionStorage.removeItem('tempSignupPassword');
          sessionStorage.removeItem('returnToAgreementDialog');

          toast(
            <p>{t('signup.success')}</p>,
            toastCommonProps("bottom-center", "toast_success", 1500)
          );

          // Navigate after a delay to ensure Redux state is saved
          setTimeout(() => {
            navigate('/home');
          }, 1600);
        } else {
          setErrorMsg(res.data.error);
          toast(
            <p>{res.data.error}</p>,
            toastCommonProps("bottom-center", "toast_alert", 2000)
          );
        }
      } catch (err) {
        console.error(err);
        setErrorMsg(t('signup.error_occurred'));
        toast(
          <p>{t('signup.error_occurred')}</p>,
          toastCommonProps("bottom-center", "toast_alert", 2000)
        );
      }
    }
  };

  const onSetPrevStep = () => {
    if (step === 2 && subStep === 2) {
      // Go back from password input to email verification
      setSubStep(1);
    } else if (step === 2 && subStep === 1) {
      // Go back to email input
      setStep(1);
      setSubStep(1);
    } else {
      // Normal step back
      setStep(step - 1);
      setSubStep(1);
    }
    setActive("ready");
  };

  const onSaveEach = (type, value) => {
    if (type === 'pass') {
      // Save password field correctly
      setSignupDatas(prev => ({ ...prev, password: value }));
    } else {
      setSignupDatas(prev => ({ ...prev, [type]: value }));
    }
  };

  const onSaveAgreement = async (items) => {
    setSignupDatas(prev => ({ ...prev, agree: [...items] }));

    // STEP 3: Create user account after Terms are agreed
    try {
      const signupParams = {
        email: signupDatas.email,
        password: signupDatas.password,
        firebaseToken: signupDatas.firebaseToken,
        accountType: 'email',
        agree: items,
      };

      const res = await api.post('/auth/email/signup', signupParams);

      if (res.data.ok) {
        // Store JWT token
        localStorage.setItem('accessToken', res.data.accessToken);

        // Store userId for later updates
        setSignupDatas(prev => ({ ...prev, userId: res.data.userId }));

        // Save login info to Redux
        dispatch(saveLoginInfo({
          userId: res.data.userId,
          nickName: res.data.nickName || signupDatas.email.split('@')[0],
          email: res.data.email,
          existPass: true,
        }));

        toast(
          <p>Account created successfully!</p>,
          toastCommonProps("bottom-center", "toast_success", 1500)
        );

        // Move to survey (step 4)
        setStep(4);
        dispatch(getNickname({ maxLength: 12 }));
      } else {
        setErrorMsg(res.data.error || t('signup.error_occurred'));
        toast(
          <p>{res.data.error || t('signup.error_occurred')}</p>,
          toastCommonProps("bottom-center", "toast_alert", 2000)
        );
      }
    } catch (err) {
      console.error('Error creating account:', err);
      setErrorMsg(t('signup.error_occurred'));
      toast(
        <p>{t('signup.error_occurred')}</p>,
        toastCommonProps("bottom-center", "toast_alert", 2000)
      );
    }
  };

  // Save signup data to sessionStorage for recovery if user views policies
  useEffect(() => {
    if (signupDatas.email) {
      sessionStorage.setItem('tempSignupEmail', signupDatas.email);
    }
    if (signupDatas.password) {
      sessionStorage.setItem('tempSignupPassword', signupDatas.password);
    }
  }, [signupDatas.email, signupDatas.password]);

  // Check if user is returning from viewing policy
  useEffect(() => {
    const savedStep = sessionStorage.getItem('signupStep');
    const savedSubStep = sessionStorage.getItem('signupSubStep');
    const savedEmail = sessionStorage.getItem('tempSignupEmail');
    const savedPassword = sessionStorage.getItem('tempSignupPassword');

    if (savedStep && savedSubStep) {
      setStep(parseInt(savedStep));
      setSubStep(parseInt(savedSubStep));

      // Restore email and password if available
      if (savedEmail || savedPassword) {
        setSignupDatas(prev => ({
          ...prev,
          ...(savedEmail && { email: savedEmail }),
          ...(savedPassword && { password: savedPassword })
        }));
      }

      // Clean up
      sessionStorage.removeItem('signupStep');
      sessionStorage.removeItem('signupSubStep');
      // Don't remove email/password yet - they might be needed if user views another policy
    }
  }, []);

  // Check if user is returning to complete registration
  useEffect(() => {
    const incompleteData = sessionStorage.getItem('incompleteRegistration');
    if (incompleteData || location.state?.continueRegistration) {
      try {
        const data = incompleteData ? JSON.parse(incompleteData) : location.state;
        if (data) {
          // Restore user data
          setSignupDatas(prev => ({
            ...prev,
            email: data.email,
            userId: data.userId,
          }));

          // Set step based on where they left off
          if (data.step) {
            setStep(data.step);
          } else if (data.registrationStep === 'terms_agreed') {
            setStep(5); // Continue from nickname (skip survey now)
            dispatch(getNickname({ maxLength: 12 }));
          } else if (data.registrationStep === 'email_verified') {
            setStep(2); // Continue from password setup
          }

          // Clear the session storage
          sessionStorage.removeItem('incompleteRegistration');

          toast(
            <p>{t('signup.continue_registration', 'Let\'s continue where you left off!')}</p>,
            toastCommonProps("bottom-center", "toast_success", 2000)
          );
        }
      } catch (err) {
        console.error('Error restoring registration data:', err);
      }
    }
  }, [location, dispatch, t]);

  useEffect(() => {
    if (step === 4) {
      const surveyLength = 8;
      if (surveyResponse && surveyResponse?.selectedNum !== surveyLength - 1) {
        setActive("on");
      } else if (
        surveyResponse?.selectedNum === surveyLength - 1 &&
        surveyResponse?.etc !== ""
      ) {
        setActive("on");
      } else {
        setActive("ready");
      }
    } else if (step === 5) {
      // Nickname is optional - always enable button
      setActive("on");
    } else if (step === 6) {
      if (!signupDatas.characterPhoto) {
        setActive("ready");
      } else {
        setActive("on");
      }
    }
  }, [step, surveyResponse, signupDatas.characterPhoto]);

  return (
    <>
      <HeadCont
        rightIcon={"no icon"}
        onLeftAction={step !== 1 ? onSetPrevStep : () => navigate(-1)}
      />

      {step === 1 ? ( // STEP 1: Email Input
        <section className="mx-6 mt-[80px] flex flex-col gap-6">
          <TitleBox option={2} title={t('signup.email_prompt')} />
          <InputBox
            option="common"
            placeholder={t('signup.email_placeholder')}
            comments={t('signup.email_comment')}
            onChangeInput={(e) => onChangeInput(e, 'email')}
            value={signupDatas.email}
            setValue={(value) => setSignupDatas(prev => ({ ...prev, email: value }))}
            setErrorMsg={setErrorMsg}
            inputM={"email"}
            refName={emailRef}
            active={active}
            errorMsg={errorMsg}
          />
        </section>
      ) : step === 2 && subStep === 1 ? ( // STEP 2.1: Email Verification
        <section className="mx-6 mt-[80px] flex flex-col gap-6">
          <TitleBox option={2} title={t('signup.verify_email', 'Verify your email')} />
          <p className="text-sm text-gray-600">
            {t('signup.verification_sent_message', 'We sent a verification code to your email. Please check your inbox and enter the code below.')}
          </p>
          <InputBox
            option="common"
            placeholder={t('signup.verification_code_placeholder', 'Enter 6-digit code')}
            comments={t('signup.verification_code_comment', 'Check your email for the 6-digit verification code')}
            onChangeInput={(e) => onChangeInput(e, 'verificationCode')}
            value={verificationCode}
            setValue={(value) => setVerificationCode(value)}
            setErrorMsg={setErrorMsg}
            refName={verificationRef}
            active={active}
            errorMsg={errorMsg}
            inputM={"number"}
          />
          <button
            onClick={async () => {
              try {
                const verifyRes = await api.post('/auth/verify-email/send', { email: signupDatas.email });
                if (verifyRes.data.ok) {
                  toast(
                    <p>{t('signup.verification_resent', 'Verification code sent again!')}</p>,
                    toastCommonProps("bottom-center", "toast_success", 2000)
                  );
                } else {
                  toast(
                    <p>{verifyRes.data.error || t('signup.verification_failed', 'Failed to resend code')}</p>,
                    toastCommonProps("bottom-center", "toast_alert", 2000)
                  );
                }
              } catch (err) {
                console.error('Error resending verification:', err);
                toast(
                  <p>{t('signup.verification_failed', 'Failed to resend code')}</p>,
                  toastCommonProps("bottom-center", "toast_alert", 2000)
                );
              }
            }}
            className="text-sm text-main underline"
          >
            {t('signup.resend_code', 'Resend verification code')}
          </button>
        </section>
      ) : step === 2 && subStep === 2 ? ( // STEP 2.2: Password
        <section className="mx-6 mt-[80px] flex flex-col gap-6">
          <TitleBox option={2} title={t('signup.create_account')} />
          {/* PassInput has its own button, which moves to step 3 (Terms) */}
          <PassInput
            onSaveEach={onSaveEach}
            onSaveAgreement={onSaveAgreement}
          />
        </section>
      ) : step === 3 ? ( // STEP 3: Terms & Agreements (PassInput's AgreementDialog is shown here)
        // The AgreementDialog is displayed by PassInput when password is saved
        // After accepting terms, user account is created via onSaveAgreement
        <div></div>
      ) : step === 4 ? ( // STEP 4: Survey
        <SignUpSurvey
          surveyResponse={surveyResponse}
          onSurveyInput={onSurveyInput}
        />
      ) : step === 5 ? ( // Nickname
        <section className="mx-6 mt-[80px] flex flex-col gap-6">
          <TitleBox option={2} title={t('signup.choose_nickname')} />
          <InputBox
            option="common"
            nameTitle={"nickName"}
            placeholder={dummyNickname}
            comments={t('signup.nickname_display')}
            onChangeInput={(e) => onChangeInput(e, 'nickName')}
            value={signupDatas.nickName}
            setValue={(value) => setSignupDatas(prev => ({ ...prev, nickName: value }))}
            setErrorMsg={setErrorMsg}
            active={active}
            errorMsg={errorMsg}
          />
          <p className="text-sm text-gray-500 -mt-2 mx-6">
            {t('signup.nickname_optional', 'Leave blank to use auto-generated nickname')}
          </p>
        </section>
      ) : ( // Character (step 6)
        <section className="mx-6 mt-[80px] flex flex-col gap-6">
          <TitleBox option={2} title={t('signup.choose_character')} />
          <CharacterInput
            onSaveEach={onSaveEach}
            character={signupDatas.characterPhoto}
          />
        </section>
      )}

      {/* Don't show button at step 2.2 (PassInput has its own button) or step 3 (Terms modal) */}
      {!(step === 2 && subStep === 2) && step !== 3 && (
        <Btn
          active={active}
          onAction={onSetNextStep}
          option={"bottom"}
          bgColor={"bg-main"}
          textColor={"text-white"}
          borderColor={"border border-main"}
          title={
            step === 1 ? t('signup.continue') :
            step === 2 && subStep === 1 ? t('signup.verify_email_button', 'Verify Email') :
            step === 4 ? t('signup.continue') :
            step === 5 ? t('signup.continue') :
            t('signup.complete')
          }
        />
      )}

    </>
  );
};

export default EmailSignUp;
